/* Nyluno theme behaviour. Vanilla JS, no dependencies.
   Every component is keyed off a data attribute, initialises at most once per
   element, supports many instances per page, and re-initialises cleanly when
   the Theme Editor reloads a section. */
(function () {
  'use strict';

  var CART_SECTIONS = 'nyluno-cart-drawer,header';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function once(el, key) {
    if (!el.__ny) el.__ny = {};
    if (el.__ny[key]) return false;
    el.__ny[key] = true;
    return true;
  }

  function on(el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    return function () { el.removeEventListener(type, fn, opts); };
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handler(e) {
      if (e.key === 'Escape') { e.preventDefault(); onEscape(); return; }
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', handler);
    return function () { document.removeEventListener('keydown', handler); };
  }

  var scrollLocks = 0;
  function lockScroll() {
    scrollLocks++;
    if (scrollLocks === 1) document.documentElement.style.overflow = 'hidden';
  }
  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.documentElement.style.overflow = '';
  }

  function announce(msg) {
    var region = document.getElementById('LiveRegion');
    if (region) region.textContent = msg;
  }

  /* ---- scroll reveal ---- */
  function initReveal(root) {
    var nodes = $$('[data-ny-reveal]', root);
    if (!nodes.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Reveal when the element enters the viewport, and also when a jump
        // scroll or a restored position has already carried it above the fold:
        // an observer only reports the current state, so anything skipped over
        // would otherwise stay invisible for good.
        if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    nodes.forEach(function (n) {
      if (!once(n, 'reveal')) return;
      n.classList.add('ny-reveal');
      obs.observe(n);
    });
  }

  /* ---- accordions ---- */
  function initAccordions(root) {
    $$('[data-ny-acc]', root).forEach(function (acc) {
      if (!once(acc, 'acc')) return;
      var single = acc.getAttribute('data-ny-acc') === 'single';
      var buttons = $$('.ny-acc__btn', acc);

      buttons.forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;

        on(btn, 'click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';
          if (single && !open) {
            buttons.forEach(function (other) {
              if (other === btn) return;
              var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
              other.setAttribute('aria-expanded', 'false');
              if (otherPanel) otherPanel.setAttribute('data-open', 'false');
            });
          }
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.setAttribute('data-open', open ? 'false' : 'true');
        });

        on(btn, 'keydown', function (e) {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          var i = buttons.indexOf(btn);
          var next = e.key === 'ArrowDown' ? (i + 1) % buttons.length : (i - 1 + buttons.length) % buttons.length;
          buttons[next].focus();
        });
      });
    });
  }

  /* ---- quantity steppers ---- */
  function initQty(root) {
    $$('[data-ny-qty]', root).forEach(function (wrap) {
      if (!once(wrap, 'qty')) return;
      var input = $('input', wrap);
      if (!input) return;

      function clamp(v) {
        var min = parseInt(input.getAttribute('min') || '1', 10);
        var maxAttr = input.getAttribute('max');
        var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
        if (isNaN(v) || v < min) v = min;
        if (v > max) v = max;
        return v;
      }

      $$('[data-ny-qty-step]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          input.value = clamp(parseInt(input.value, 10) + parseInt(btn.getAttribute('data-ny-qty-step'), 10));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      on(input, 'change', function () { input.value = clamp(parseInt(input.value, 10)); });
    });
  }

  /* ---- media gallery ---- */
  function initGalleries(root) {
    $$('[data-ny-gallery]', root).forEach(function (gallery) {
      if (!once(gallery, 'gallery')) return;

      var track = $('[data-ny-gallery-track]', gallery);
      var slides = $$('.ny-gallery__slide', gallery);
      var thumbs = $$('[data-ny-gallery-thumb]', gallery);
      var counter = $('[data-ny-gallery-counter]', gallery);
      var scroller = $('.ny-gallery__scroll', gallery);
      if (!track || slides.length === 0) return;

      var index = 0;

      function pauseAll() {
        $$('video', gallery).forEach(function (v) { if (!v.paused) v.pause(); });
      }

      function show(next, focusThumb) {
        index = (next + slides.length) % slides.length;
        track.style.transform = 'translateX(' + (-100 * index) + '%)';
        pauseAll();
        slides.forEach(function (s, i) {
          s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          $$(FOCUSABLE, s).forEach(function (el) {
            if (i === index) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });
        thumbs.forEach(function (t, i) { t.setAttribute('aria-selected', i === index ? 'true' : 'false'); });
        if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
        if (thumbs[index] && scroller) {
          var t = thumbs[index];
          var left = t.offsetLeft - (scroller.clientWidth - t.clientWidth) / 2;
          scroller.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          if (focusThumb) t.focus();
        }
      }

      $$('[data-ny-gallery-nav]', gallery).forEach(function (btn) {
        on(btn, 'click', function () { show(index + parseInt(btn.getAttribute('data-ny-gallery-nav'), 10)); });
      });

      thumbs.forEach(function (thumb, i) {
        on(thumb, 'click', function () { show(i); });
        on(thumb, 'keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1, true); }
        });
      });

      var x0 = null, y0 = null;
      on(track, 'touchstart', function (e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      on(track, 'touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1));
        x0 = null; y0 = null;
      }, { passive: true });

      gallery.__nySelectMedia = function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === String(mediaId)) { show(i); return; }
        }
      };

      show(0);
    });
  }

  /* ---- click-to-play video ---- */
  function initVideos(root) {
    $$('[data-ny-video]', root).forEach(function (wrap) {
      if (!once(wrap, 'video')) return;
      var btn = $('[data-ny-video-play]', wrap);
      if (!btn) return;

      on(btn, 'click', function () {
        var video = $('video', wrap);
        var embed = wrap.getAttribute('data-ny-video-embed');
        wrap.classList.add('is-playing');
        if (video) { video.play(); return; }
        if (embed) {
          var iframe = document.createElement('iframe');
          iframe.setAttribute('src', embed + (embed.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1');
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('title', wrap.getAttribute('data-ny-video-title') || 'Video');
          wrap.appendChild(iframe);
        }
      });
    });
  }

  /* ---- announcement rotator ---- */
  function initAnnounce(root) {
    $$('[data-ny-announce]', root).forEach(function (bar) {
      if (!once(bar, 'announce')) return;
      var items = $$('.ny-announce__item', bar);
      if (items.length < 2) return;
      var every = parseInt(bar.getAttribute('data-ny-announce'), 10) || 5;
      var i = 0;
      var timer = setInterval(function () {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, every * 1000);
      bar.__nyCleanup = function () { clearInterval(timer); };
    });
  }

  /* ---- mobile navigation ---- */
  function initMobileNav(root) {
    $$('[data-ny-mobile-nav]', root).forEach(function (nav) {
      if (!once(nav, 'mobilenav')) return;
      var opener = document.querySelector('[data-ny-mobile-nav-open]');
      var releaseTrap = null, lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        nav.classList.add('is-open');
        if (opener) opener.setAttribute('aria-expanded', 'true');
        lockScroll();
        releaseTrap = trapFocus(nav, close);
        var first = $(FOCUSABLE, nav);
        if (first) first.focus();
      }
      function close() {
        if (!nav.classList.contains('is-open')) return;
        nav.classList.remove('is-open');
        if (opener) opener.setAttribute('aria-expanded', 'false');
        unlockScroll();
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      if (opener) on(opener, 'click', open);
      $$('[data-ny-mobile-nav-close]', nav).forEach(function (btn) { on(btn, 'click', close); });
      nav.__nyCleanup = function () { if (releaseTrap) releaseTrap(); if (nav.classList.contains('is-open')) unlockScroll(); };
    });
  }

  /* ---- cart ---- */
  var Cart = {
    root: function () { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; },
    drawer: function () { return document.querySelector('[data-ny-drawer]'); },
    releaseTrap: null,
    lastFocus: null,

    open: function () {
      var drawer = Cart.drawer();
      if (!drawer) return;
      Cart.lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      lockScroll();
      Cart.releaseTrap = trapFocus(drawer, Cart.close);
      requestAnimationFrame(function () {
        var target = $('[data-ny-drawer-close]', drawer) || $(FOCUSABLE, drawer);
        if (target) target.focus();
      });
    },

    close: function () {
      var drawer = Cart.drawer();
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      unlockScroll();
      if (Cart.releaseTrap) { Cart.releaseTrap(); Cart.releaseTrap = null; }
      if (Cart.lastFocus && Cart.lastFocus.focus) Cart.lastFocus.focus();
    },

    busy: function (state) {
      var drawer = Cart.drawer();
      if (drawer) drawer.classList.toggle('is-busy', !!state);
    },

    render: function (sections) {
      if (!sections) return;
      var parser = new DOMParser();

      if (sections['nyluno-cart-drawer']) {
        var doc = parser.parseFromString(sections['nyluno-cart-drawer'], 'text/html');
        var fresh = doc.querySelector('[data-ny-drawer-contents]');
        var current = document.querySelector('[data-ny-drawer-contents]');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          initAll(current);
        }
        var freshTitle = doc.querySelector('[data-ny-drawer-title]');
        var curTitle = document.querySelector('[data-ny-drawer-title]');
        if (freshTitle && curTitle) curTitle.innerHTML = freshTitle.innerHTML;
      }

      if (sections.header) {
        var hdoc = parser.parseFromString(sections.header, 'text/html');
        var freshCount = hdoc.querySelector('[data-ny-cart-count]');
        var curCount = document.querySelector('[data-ny-cart-count]');
        if (freshCount && curCount) {
          curCount.textContent = freshCount.textContent;
          if (freshCount.hasAttribute('hidden')) curCount.setAttribute('hidden', '');
          else curCount.removeAttribute('hidden');
          var link = curCount.closest('[data-ny-cart-link]');
          var freshLink = freshCount.closest('[data-ny-cart-link]');
          if (link && freshLink) link.setAttribute('aria-label', freshLink.getAttribute('aria-label'));
        }
      }
    },

    request: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
          return data;
        });
      });
    },

    change: function (line, quantity) {
      Cart.busy(true);
      return Cart.request(Cart.root() + 'cart/change.js', { line: line, quantity: quantity, sections: CART_SECTIONS })
        .then(function (data) {
          Cart.render(data.sections);
          announce(quantity === 0 ? 'Item removed from cart' : 'Cart updated');
        })
        .catch(function (err) { Cart.showError(err.message); })
        .then(function () { Cart.busy(false); });
    },

    showError: function (msg) {
      var host = document.querySelector('[data-ny-drawer-error]');
      if (!host) { announce(msg); return; }
      host.textContent = msg;
      host.removeAttribute('hidden');
      announce(msg);
    }
  };

  function initDrawer(root) {
    $$('[data-ny-drawer]', root).forEach(function (drawer) {
      if (!once(drawer, 'drawer')) return;
      var scrim = $('.ny-drawer__scrim', drawer);
      if (scrim) on(scrim, 'click', Cart.close);
    });

    $$('[data-ny-drawer-close]', root).forEach(function (btn) {
      if (!once(btn, 'drawerclose')) return;
      on(btn, 'click', Cart.close);
    });

    $$('[data-ny-cart-open]', root).forEach(function (btn) {
      if (!once(btn, 'cartopen')) return;
      on(btn, 'click', function (e) {
        if (!document.querySelector('[data-ny-drawer]')) return;
        e.preventDefault();
        Cart.open();
      });
    });
  }

  function initDrawerControls(root) {
    $$('[data-ny-line-qty]', root).forEach(function (wrap) {
      if (!once(wrap, 'lineqty')) return;
      var input = $('input', wrap);
      var line = parseInt(wrap.getAttribute('data-ny-line-qty'), 10);
      if (!input) return;

      $$('[data-ny-qty-step]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var next = parseInt(input.value, 10) + parseInt(btn.getAttribute('data-ny-qty-step'), 10);
          if (next < 0) next = 0;
          Cart.change(line, next);
        });
      });
      on(input, 'change', function () {
        var v = parseInt(input.value, 10);
        Cart.change(line, isNaN(v) || v < 0 ? 0 : v);
      });
    });

    $$('[data-ny-line-remove]', root).forEach(function (btn) {
      if (!once(btn, 'lineremove')) return;
      on(btn, 'click', function () { Cart.change(parseInt(btn.getAttribute('data-ny-line-remove'), 10), 0); });
    });
  }

  /* ---- product form ---- */
  function groupDigits(intPart, sep) {
    var out = '', count = 0;
    for (var i = intPart.length - 1; i >= 0; i--) {
      out = intPart.charAt(i) + out;
      count++;
      if (count % 3 === 0 && i > 0) out = sep + out;
    }
    return out;
  }

  /* Shopify applies a percentage discount per unit and rounds the per-unit
     amount down to the cent, then multiplies by the quantity. Mirroring that
     exactly is what keeps a displayed bundle total equal to the cart total.
     This only affects what is DISPLAYED: the line is still added at full
     price and Shopify applies the real discount once, in the cart. */
  function unitNetPrice(price, percent) {
    if (!percent || percent <= 0) return price;
    return price - Math.floor(price * percent / 100);
  }

  function initProductForms(root) {
    $$('[data-ny-product]', root).forEach(function (scope) {
      if (!once(scope, 'product')) return;

      var form = $('form[data-ny-product-form]', scope);
      var variantsNode = $('[data-ny-variants]', scope);
      if (!form || !variantsNode) return;

      var variants;
      try { variants = JSON.parse(variantsNode.textContent); } catch (e) { variants = []; }

      var idInput = $('[data-ny-variant-id]', form);
      var submit = $('[data-ny-submit]', form);
      var status = $('[data-ny-status]', scope);
      var gallery = $('[data-ny-gallery]', scope) || document.querySelector('[data-ny-gallery]');
      var qtyInput = $('[data-ny-qty] input', form) || $('input[name="quantity"]', form);
      var offerInputs = $$('[data-ny-offer-qty]', scope);
      var fmt = scope.getAttribute('data-ny-money-format') || '${{amount}}';
      var showAtcPrice = scope.getAttribute('data-ny-atc-price') === 'true';

      function money(cents) {
        var negative = cents < 0;
        var abs = Math.abs(cents);
        var whole = Math.floor(abs / 100);
        var frac = ('0' + (abs % 100)).slice(-2);
        var out = fmt;
        var withDot = groupDigits(String(whole), ',') + '.' + frac;
        var withComma = groupDigits(String(whole), '.') + ',' + frac;
        var noDec = groupDigits(String(Math.round(abs / 100)), ',');
        var noDecComma = groupDigits(String(Math.round(abs / 100)), '.');
        var pairs = [
          ['{{amount}}', withDot], ['{{ amount }}', withDot],
          ['{{amount_with_comma_separator}}', withComma], ['{{ amount_with_comma_separator }}', withComma],
          ['{{amount_no_decimals}}', noDec], ['{{ amount_no_decimals }}', noDec],
          ['{{amount_no_decimals_with_comma_separator}}', noDecComma],
          ['{{ amount_no_decimals_with_comma_separator }}', noDecComma]
        ];
        for (var i = 0; i < pairs.length; i++) out = out.split(pairs[i][0]).join(pairs[i][1]);
        return (negative ? '-' : '') + out;
      }

      function currentVariant() {
        var chosen = $$('[data-ny-option]', scope)
          .filter(function (i) { return i.checked; })
          .sort(function (a, b) { return a.getAttribute('data-ny-option') - b.getAttribute('data-ny-option'); })
          .map(function (i) { return i.value; });

        if (!chosen.length) return variants[0];
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < chosen.length; j++) {
            if (v.options[j] !== chosen[j]) { match = false; break; }
          }
          if (match) return v;
        }
        return null;
      }

      // Keep the highlighted quantity option and the quantity field in step,
      // in both directions, so the selected card always matches what is added.
      function syncOffersToQty() {
        if (!qtyInput || !offerInputs.length) return;
        var q = parseInt(qtyInput.value, 10);
        offerInputs.forEach(function (input) {
          input.checked = parseInt(input.getAttribute('data-ny-offer-qty'), 10) === q;
        });
      }

      // The automatic discount attached to whichever quantity option is chosen.
      function selectedDiscount() {
        for (var i = 0; i < offerInputs.length; i++) {
          if (offerInputs[i].checked) {
            return parseFloat(offerInputs[i].getAttribute('data-ny-offer-discount')) || 0;
          }
        }
        return 0;
      }

      function paint(variant) {
        var soldOut = !variant || !variant.available;
        var discounted = variant && variant.compare_at_price > variant.price;

        var q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (!q || q < 1) q = 1;
        var off = selectedDiscount();
        var lineTotal = variant ? unitNetPrice(variant.price, off) * q : 0;
        var lineTotalText = variant ? money(lineTotal) : '';

        if (idInput && variant) idInput.value = variant.id;

        if (submit) {
          submit.disabled = soldOut;
          var label = $('[data-ny-submit-label]', submit) || submit;
          if (soldOut) {
            label.textContent = submit.getAttribute('data-label-soldout') || 'Sold out';
          } else {
            var addText = submit.getAttribute('data-label-add') || 'Add to cart';
            label.textContent = (showAtcPrice && variant) ? addText + ' — ' + lineTotalText : addText;
          }
        }

        $$('[data-ny-price]', scope).forEach(function (el) { el.textContent = variant ? money(variant.price) : ''; });

        $$('[data-ny-compare]', scope).forEach(function (el) {
          if (discounted) { el.textContent = money(variant.compare_at_price); el.removeAttribute('hidden'); }
          else { el.setAttribute('hidden', ''); }
        });

        $$('[data-ny-savings]', scope).forEach(function (el) {
          if (discounted) {
            var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
            var tpl = el.getAttribute('data-label') || 'Save {{pct}}%';
            el.textContent = tpl.split('{{pct}}').join(String(pct));
            el.removeAttribute('hidden');
          } else { el.setAttribute('hidden', ''); }
        });

        // Quantity-offer totals stay in step with the live variant price, and
        // with any automatic discount configured for that quantity.
        $$('[data-ny-offer-total]', scope).forEach(function (el) {
          var oq = parseInt(el.getAttribute('data-ny-offer-total'), 10) || 1;
          var ooff = parseFloat(el.getAttribute('data-ny-offer-discount')) || 0;
          el.textContent = variant ? money(unitNetPrice(variant.price, ooff) * oq) : '';
        });

        // With a quantity discount, the struck-through figure is the undiscounted
        // line total. Without one, it falls back to the compare-at line total.
        $$('[data-ny-offer-was]', scope).forEach(function (el) {
          var oq = parseInt(el.getAttribute('data-ny-offer-was'), 10) || 1;
          var ooff = parseFloat(el.getAttribute('data-ny-offer-discount')) || 0;
          if (!variant) { el.setAttribute('hidden', ''); return; }
          if (ooff > 0) { el.textContent = money(variant.price * oq); el.removeAttribute('hidden'); }
          else if (discounted) { el.textContent = money(variant.compare_at_price * oq); el.removeAttribute('hidden'); }
          else { el.setAttribute('hidden', ''); }
        });

        // Per-unit line, reflecting the chosen quantity and its discount.
        $$('[data-ny-perunit]', scope).forEach(function (el) {
          if (!variant) { el.textContent = ''; return; }
          var per = el.getAttribute('data-per-word') || 'per';
          var one = el.getAttribute('data-unit-one') || '';
          var many = el.getAttribute('data-unit-many') || one;
          var text = money(unitNetPrice(variant.price, off)) + ' ' + per + ' ' + one;
          if (q > 1) text += ' · ' + q + ' ' + many;
          el.textContent = text;
        });

        $$('[data-ny-stock]', scope).forEach(function (el) {
          el.textContent = soldOut ? (el.getAttribute('data-label-out') || 'Sold out') : (el.getAttribute('data-label-in') || 'In stock');
          el.classList.toggle('ny-stock--out', soldOut);
        });

        if (variant && variant.featured_media_id && gallery && gallery.__nySelectMedia) {
          gallery.__nySelectMedia(variant.featured_media_id);
        }

        document.dispatchEvent(new CustomEvent('ny:variant', {
          detail: {
            variant: variant,
            scope: scope,
            quantity: q,
            discountPercent: off,
            lineTotal: lineTotal,
            lineTotalFormatted: lineTotalText
          }
        }));
      }

      $$('[data-ny-option]', scope).forEach(function (input) {
        on(input, 'change', function () { paint(currentVariant()); });
      });

      offerInputs.forEach(function (input) {
        on(input, 'change', function () {
          if (qtyInput) {
            qtyInput.value = input.getAttribute('data-ny-offer-qty');
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            paint(currentVariant());
          }
        });
      });

      if (qtyInput) {
        on(qtyInput, 'change', function () { syncOffersToQty(); paint(currentVariant()); });
        syncOffersToQty();
      }

      on(form, 'submit', function (e) {
        e.preventDefault();
        if (submit && submit.disabled) return;

        var useDrawer = !!document.querySelector('[data-ny-drawer]');
        if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }
        if (status) { status.textContent = ''; status.classList.remove('ny-buy__status--error'); }

        var body = new FormData(form);
        body.append('sections', CART_SECTIONS);

        fetch(Cart.root() + 'cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: body })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
              return data;
            });
          })
          .then(function (data) {
            if (status) status.textContent = form.getAttribute('data-added-text') || 'Added to your cart';
            announce('Added to your cart');
            if (useDrawer) { Cart.render(data.sections); Cart.open(); }
            else window.location = Cart.root() + 'cart';
          })
          .catch(function (err) {
            if (status) { status.textContent = err.message; status.classList.add('ny-buy__status--error'); }
            announce(err.message);
          })
          .then(function () {
            if (submit) submit.removeAttribute('aria-busy');
            paint(currentVariant());
          });
      });

      paint(currentVariant());
    });
  }

  /* ---- sticky add to cart ---- */
  function initSticky(root) {
    $$('[data-ny-sticky]', root).forEach(function (bar) {
      if (!once(bar, 'sticky')) return;
      var anchor = document.querySelector(bar.getAttribute('data-ny-sticky'));
      if (!anchor || !('IntersectionObserver' in window)) return;

      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', entry.boundingClientRect.top < 0 && !entry.isIntersecting);
        });
      }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });
      obs.observe(anchor);

      $$('[data-ny-sticky-submit]', bar).forEach(function (btn) {
        on(btn, 'click', function () {
          var form = document.querySelector('form[data-ny-product-form]');
          if (!form) return;
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      });

      // The sticky bar shows what the buyer is about to pay: the selected
      // quantity option's total, discount included — not the unit price.
      var onVariant = function (e) {
        if (!e.detail) return;
        var v = e.detail.variant;
        if (e.detail.lineTotalFormatted) {
          $$('[data-ny-sticky-price]', bar).forEach(function (el) {
            el.textContent = e.detail.lineTotalFormatted;
          });
        }
        $$('[data-ny-sticky-qty]', bar).forEach(function (el) {
          var q = e.detail.quantity || 1;
          el.textContent = q > 1 ? '× ' + q : '';
          if (q > 1) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
        });
        $$('[data-ny-sticky-submit]', bar).forEach(function (btn) { btn.disabled = !v || !v.available; });
      };
      document.addEventListener('ny:variant', onVariant);

      bar.__nyCleanup = function () {
        obs.disconnect();
        document.removeEventListener('ny:variant', onVariant);
      };
    });
  }

  /* ---- back to top ---- */
  function initToTop(root) {
    $$('[data-ny-totop]', root).forEach(function (btn) {
      if (!once(btn, 'totop')) return;
      var onScroll = function () { btn.classList.toggle('is-visible', window.scrollY > window.innerHeight); };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      on(btn, 'click', function () {
        window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
      btn.__nyCleanup = function () { window.removeEventListener('scroll', onScroll); };
    });
  }

  /* ---- boot ---- */
  function initAll(root) {
    root = root || document;
    initReveal(root);
    initAccordions(root);
    initQty(root);
    initGalleries(root);
    initVideos(root);
    initAnnounce(root);
    initMobileNav(root);
    initDrawer(root);
    initDrawerControls(root);
    initProductForms(root);
    initSticky(root);
    initToTop(root);
  }

  function cleanup(root) {
    var all = $$('*', root);
    all.push(root);
    all.forEach(function (el) {
      if (el && typeof el.__nyCleanup === 'function') { el.__nyCleanup(); el.__nyCleanup = null; }
    });
  }

  document.documentElement.classList.remove('no-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { cleanup(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var btn = e.target.querySelector ? e.target.querySelector('.ny-acc__btn') : null;
    if (btn && btn.getAttribute('aria-expanded') === 'false') btn.click();
  });

  window.NylunoCart = Cart;
})();
